/*
 * Copyright 2026 North Carolina State University
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

const POLL_INTERVAL = 30000; // 30 seconds

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [panelOpen, setPanelOpen] = useState(false);

  const { data: unreadCount = 0 } = useQuery(
    'notificationUnreadCount',
    async () => {
      const res = await axios.get('/api/notifications/unread-count');
      return res.data.unreadCount;
    },
    {
      enabled: !!user,
      refetchInterval: POLL_INTERVAL,
      refetchOnWindowFocus: true,
      staleTime: 10000,
    }
  );

  const { data: notificationsData, isLoading: loadingNotifications } = useQuery(
    ['notifications', panelOpen],
    async () => {
      const res = await axios.get('/api/notifications', {
        params: { page: 1, limit: 30 }
      });
      return res.data;
    },
    {
      enabled: !!user && panelOpen,
      staleTime: 5000,
    }
  );

  const notifications = notificationsData?.notifications || [];

  const togglePanel = useCallback(() => {
    setPanelOpen(prev => !prev);
  }, []);

  const closePanel = useCallback(() => {
    setPanelOpen(false);
  }, []);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      await axios.patch(`/api/notifications/${notificationId}/read`);
      queryClient.invalidateQueries('notificationUnreadCount');
      queryClient.invalidateQueries('notifications');
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  }, [queryClient]);

  const markAllAsRead = useCallback(async () => {
    try {
      await axios.patch('/api/notifications/read-all');
      queryClient.invalidateQueries('notificationUnreadCount');
      queryClient.invalidateQueries('notifications');
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
    }
  }, [queryClient]);

  const refreshNotifications = useCallback(() => {
    queryClient.invalidateQueries('notificationUnreadCount');
    queryClient.invalidateQueries('notifications');
  }, [queryClient]);

  return (
    <NotificationContext.Provider value={{
      unreadCount,
      notifications,
      loadingNotifications,
      panelOpen,
      togglePanel,
      closePanel,
      markAsRead,
      markAllAsRead,
      refreshNotifications,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
